import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Checkbox
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import models.DomainGame
import models.GameSettings
import models.Player
import org.jetbrains.compose.resources.stringResource
import banko.composeapp.generated.resources.Res
import banko.composeapp.generated.resources.*

@Composable
internal fun GameScreenBottomSheet(
    uiState: BankoGameScreenUiState,
    game: DomainGame?,
    gameState: GameScreenState,
    shouldShowManualDiceGrid: Boolean,
    showPlayerPickerDialog: MutableState<Boolean>,
    isRolling: MutableState<Boolean>,
    navigateBack: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    if (showPlayerPickerDialog.value) {
        PlayerPickerDialog(
            showPlayerPickerDialog = showPlayerPickerDialog,
            uiState = uiState
        )
    }

    Column(modifier = modifier.padding(horizontal = 8.dp)) {
        if (!gameState.isStarted && gameState.isHost) {
            StartGameHostAction(
                playerCount = game?.players?.size ?: 0,
                buttonHeight = 54.dp,
                onStartGame = uiState.onStartGame,
                modifier = Modifier
                    .fillMaxWidth(),
            )
        }

        if (game?.round != null) {
            if (gameState.isStarted && !gameState.isGameOver) {
                // Both primary actions pinned side by side, matching the wide/landscape action
                // cluster — on short phones the board content can scroll, but the way to act never
                // leaves the screen.
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    if (uiState.gameCode != "LOCAL" && !gameState.canActOnCurrentTurn) {
                        TableReactionActionButton(
                            uiState = uiState,
                            fontSize = 17.sp,
                            modifier = Modifier
                                .weight(1f)
                                .height(54.dp),
                        )
                    } else {
                        GlossyPrimaryButton(
                            text = stringResource(Res.string.roll_dice),
                            onClick = { uiState.onDiceRolled(null) },
                            enabled = gameState.canActOnCurrentTurn && !isRolling.value,
                            leadingIcon = BankoIcons.Casino,
                            fontSize = 17.sp,
                            modifier = Modifier
                                .weight(1f)
                                .height(54.dp),
                        )
                    }
                    BankoActionButton(
                        uiState = uiState,
                        game = game,
                        gameState = gameState,
                        showPlayerPickerDialog = showPlayerPickerDialog,
                        fontSize = 17.sp,
                        modifier = Modifier
                            .weight(1f)
                            .height(54.dp),
                    )
                }

                if (shouldShowManualDiceGrid) {
                    Spacer(modifier = Modifier.height(48.dp))
                    FourByThreeBoxGrid(
                        rollNumber = game.round.currentRoll,
                        onManuallyEnteredDice = uiState.onDiceRolled
                    )
                }
            } else if (gameState.isHost && gameState.isGameOver) {
                Button(
                    modifier = Modifier
                        .height(54.dp)
                        .fillMaxWidth(),
                    onClick = {
                        if (uiState.gameCode == "LOCAL") {
                            // For local games, just navigate back since there's no network cleanup needed
                            navigateBack()
                        } else {
                            uiState.onGameFinished()
                        }
                    },
                    shape = RoundedCornerShape(16.dp),
                ) {
                    Text(
                        text = stringResource(Res.string.finish_game),
                        fontSize = 18.sp,
                        fontWeight = FontWeight.SemiBold,
                    )
                }
            }
        }
    }
}

@Composable
internal fun BankoActionButton(
    uiState: BankoGameScreenUiState,
    game: DomainGame,
    gameState: GameScreenState,
    showPlayerPickerDialog: MutableState<Boolean>,
    modifier: Modifier = Modifier,
    fontSize: TextUnit = 20.sp,
) {
    val plan = remember(game, gameState, uiState.playerName) {
        bankoActionPlan(game, gameState, uiState.playerName)
    }

    GlossyPrimaryButton(
        text = stringResource(
            if (plan.opensPicker) Res.string.banko_for_players else Res.string.banko_button
        ),
        enabled = plan.enabled,
        fontSize = fontSize,
        modifier = modifier,
        onClick = {
            if (plan.opensPicker) {
                showPlayerPickerDialog.value = true
            } else {
                uiState.onBank(game.currentPlayer, listOf(plan.directTarget))
            }
        },
    )
}

@Composable
internal fun PlayerPickerDialog(
    showPlayerPickerDialog: MutableState<Boolean>,
    uiState: BankoGameScreenUiState
) {
    val game by uiState.gameFlow.collectAsState()
    val bankList by uiState.bankListFlow.collectAsState()

    AlertDialog(
        onDismissRequest = { showPlayerPickerDialog.value = false },
        confirmButton = {
            Button(
                enabled = bankList.isNotEmpty(),
                onClick = {
                    uiState.onGlobalBankClicked()
                    showPlayerPickerDialog.value = false
                },
                shape = RoundedCornerShape(16.dp),
            ) {
                Text(text = stringResource(Res.string.banko_for_selected_players))
            }
        },
        title = { Text(text = stringResource(Res.string.pick_player_to_banko)) },
        text = {
            Column {
                val playersToUse = remember(game) { bankablePlayers(game) }
                playersToUse.forEach { player ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(8.dp)
                            .clickable {
                                uiState.updatePlayerBankList(player.name)
                            },
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(text = player.name)
                        Checkbox(
                            checked = bankList.contains(player.name),
                            onCheckedChange = { checkValue ->
                                uiState.updatePlayerBankList(player.name)
                            },
                        )
                    }
                }
            }
        },
    )
}


@Composable
fun FourByThreeBoxGrid(rollNumber: Int = 1, onManuallyEnteredDice: (Int) -> Unit = {}) {
    val rollNumberGreaterThan3 = rollNumber > 3
    val enabledList =
        if (rollNumberGreaterThan3) listOf(3, 4, 5, 6, 7, 8, 9, 10, 11, 13) else listOf(
            2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12
        )

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .height(200.dp)
    ) {
        (2..12 step 4).forEach { row ->
            Row(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(4.dp, Alignment.CenterHorizontally)
            ) {
                (row until row + 4).forEach { i ->
                    val isEnabled = (i in enabledList) || (i == 13 && rollNumberGreaterThan3)
                    Button(
                        onClick = { onManuallyEnteredDice(i) },
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(3.dp),
                        enabled = isEnabled,
                    ) {
                        if (i == 13) {
                            Text(text = stringResource(Res.string.doubles), fontSize = 8.sp, maxLines = 1)
                        } else {
                            Text(text = i.toString())
                        }
                    }
                }
            }
        }
    }
}
